import React, { forwardRef } from 'react';
import { cn } from '../../../utils/cn';

/**
 * GlassCard - Glass morphism card component
 *
 * A versatile card component featuring glass morphism effects with configurable
 * variants, hover states, and padding options. Supports both light and dark themes.
 *
 * @example
 * ```tsx
 * <GlassCard variant="strong" hover interactive padding="lg">
 *   <h2>Card Title</h2>
 *   <p>Card content</p>
 * </GlassCard>
 * ```
 */

export interface GlassCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Visual style variant */
  variant?: 'default' | 'strong' | 'subtle';
  /** Enable hover-lift effect (translateY + shadow) */
  hover?: boolean;
  /** Enable click states (translateY on hover, ideal for clickable cards) */
  interactive?: boolean;
  /** Internal padding size */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Border radius size */
  rounded?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Additional CSS classes */
  className?: string;
  /** Card content */
  children: React.ReactNode;
  /** Click handler (automatically enables cursor pointer) */
  onClick?: () => void;
}

const paddingMap = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

const roundedMap = {
  sm: 'rounded-lg', // 8px
  md: 'rounded-xl', // 12px
  lg: 'rounded-2xl', // 16px
  xl: 'rounded-3xl', // 20px
  '2xl': 'rounded-[24px]',
};

/**
 * GlassCard Component
 *
 * Features:
 * - Glass morphism with backdrop blur
 * - Multiple visual variants (default, strong, subtle)
 * - Hover and interactive states
 * - Configurable padding and border radius
 * - Theme-aware (light/dark mode support)
 * - Accessible with proper semantic HTML
 */
export const GlassCard = forwardRef<HTMLDivElement, GlassCardProps>(
  (
    {
      variant = 'default',
      hover = false,
      interactive = false,
      padding = 'md',
      rounded = 'lg',
      className,
      children,
      onClick,
      ...props
    },
    ref
  ) => {
    // Determine glass variant class
    const glassClass = {
      default: 'glass',
      strong: 'glass-strong',
      subtle: 'glass-subtle',
    }[variant];

    // Combine all CSS classes
    const cardClasses = cn(
      glassClass,
      paddingMap[padding],
      roundedMap[rounded],
      {
        'hover-lift': hover,
        interactive: interactive,
        'cursor-pointer': onClick !== undefined || interactive,
      },
      className
    );

    return (
      <div ref={ref} className={cardClasses} onClick={onClick} {...props}>
        {children}
      </div>
    );
  }
);

GlassCard.displayName = 'GlassCard';
